import React, { useState, useEffect } from 'react';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';

// Componente para efeito de texto digitado
const TypewriterText: React.FC<{ text: string; speed?: number; onComplete?: () => void }> = ({ text, speed = 42, onComplete }) => {
    const [displayText, setDisplayText] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        if (currentIndex < text.length) {
            const timeout = setTimeout(() => {
                setDisplayText(prev => prev + text[currentIndex]);
                setCurrentIndex(prev => prev + 1);
            }, speed);
            return () => clearTimeout(timeout);
        } else if (currentIndex === text.length && onComplete) {
            onComplete();
        }
    }, [currentIndex, text, speed, onComplete]);

    useEffect(() => {
        setDisplayText('');
        setCurrentIndex(0);
    }, [text]);

    return <span>{displayText}</span>;
};

interface TutorialVotacaoPresidenteProps {
    isTutorialMode: boolean;
    onTutorialComplete: () => void;
    onOpacityChange: (naoIniciado: number, andamento: number, finalizado: number) => void;
    onShowModal: (show: boolean) => void;
    onHighlightButton: (buttonClass: string | null) => void;
    onUpdateVoteCount: (count: number) => void;
}

// Constantes de tempo para animações do tutorial (em milissegundos)
const ANIMATION_TIMES = {
    FADE_OUT_ALL: 1600,           // Etapa 4: fade out todos os candidatos
    CANDIDATE_FADE_IN_STEP5: 900,     // Etapa 5: fade in candidato na seção não iniciados
    CANDIDATE_MOVE_TO_PROGRESS: 0, // Etapa 7: candidato move para andamento
    VOTE_COUNT_INTERVAL: 1500,    // Etapa 8: intervalo entre incrementos de voto
    CANDIDATE_FADE_OUT: 2000,     // Etapa 9: fade out candidato em andamento
    CANDIDATE_FADE_IN: 900,      // Etapa 10: fade in candidato finalizado
    CANDIDATE_FADE_IN_STEP11: 500 // Etapa 11: fade in candidato na seção não iniciados
};

// Controle de etapas do tutorial
const TUTORIAL_CONFIG = {
    ENABLE_STEP_13: false,        // Etapa 13: modal de finalizar banca (desabilitada)
    ENABLE_STEP_14: false         // Etapa 14: envio de email para candidatos (desabilitada)
};

const TutorialVotacaoPresidente: React.FC<TutorialVotacaoPresidenteProps> = ({
    isTutorialMode,
    onTutorialComplete,
    onOpacityChange,
    onShowModal,
    onHighlightButton,
    onUpdateVoteCount
}) => {
    const [run, setRun] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const [candidatoNaoIniciadoOpacity, setCandidatoNaoIniciadoOpacity] = useState(1);
    const [candidatoAndamentoOpacity, setCandidatoAndamentoOpacity] = useState(1);
    const [candidatoFinalizadoOpacity, setCandidatoFinalizadoOpacity] = useState(1);
    const [pendingAnimation, setPendingAnimation] = useState<any>(null);

    const stepTexts = [
        'Candidatos comparecentes aparecem por ordem de chegada, conforme controle do Técnico da Banca',
        'Esta seção é o destino do Candidato quando o Presidente inicia sua Heteroidentificação',
        'Registra os Candidatos que já passaram pela Banca',
        'Agora acompanharemos o fluxo básico de um Candidato',
        'Vinculação do Candidato à Banca realizada pelo Técnico',
        'O Presidente pode iniciar a Heteroidentificação do Candidato',
        'Candidato teve Heteroidentificação iniciada. Está na seção \'Em Andamento\'',
        `Votar: Validado ou Não Validado`,
        'Finalizar a Heteroidentificação do Candidato',
        'Heteroidentificação encerrada; o fluxo reinicia para o próximo Candidato',
        'Ao fim da Banca, candidatos que não compareceram são marcados como Ausente pelo Presidente, conforme orientação do Técnico',
        'Finalização dos trabalhos da Banca - Ao clicar, aparece janela de confirmação'
    ];

    // Configuração das animações por etapa
    const stepAnimations = [
        null, // Etapa 1: sem animação
        null, // Etapa 2: sem animação
        null, // Etapa 3: sem animação
        {     // Etapa 4: Fade out todos os candidatos
            type: 'fadeOutAll',
            duration: ANIMATION_TIMES.FADE_OUT_ALL,
            opacity: { naoIniciado: 0, andamento: 0, finalizado: 0 }
        },
        {     // Etapa 5: Fade in candidato na seção não iniciados
            type: 'fadeIn',
            duration: ANIMATION_TIMES.CANDIDATE_FADE_IN_STEP5,
            target: 'naoIniciado',
            opacity: { naoIniciado: 1, andamento: 0, finalizado: 0 }
        },
        null, // Etapa 6: sem animação
        {     // Etapa 7: Candidato move para seção em andamento
            type: 'move',
            duration: ANIMATION_TIMES.CANDIDATE_MOVE_TO_PROGRESS,
            opacity: { naoIniciado: 0, andamento: 1, finalizado: 0 }
        },
        {     // Etapa 8: Iniciar contagem de votos
            type: 'voteCount',
            duration: ANIMATION_TIMES.VOTE_COUNT_INTERVAL,
            maxCount: 5
        },
        {     // Etapa 9: Fade out candidato em andamento
            type: 'fadeOut',
            duration: ANIMATION_TIMES.CANDIDATE_FADE_OUT,
            target: 'andamento',
            opacity: { naoIniciado: 0, andamento: 0, finalizado: 0 }
        },
        {     // Etapa 10: Fade in candidato finalizado
            type: 'fadeIn',
            duration: ANIMATION_TIMES.CANDIDATE_FADE_IN,
            target: 'finalizado',
            opacity: { naoIniciado: 0, andamento: 0, finalizado: 1 }
        },
        {     // Etapa 11: Fade in candidato na seção não iniciados
            type: 'fadeIn',
            duration: ANIMATION_TIMES.CANDIDATE_FADE_IN_STEP11,
            target: 'naoIniciado',
            opacity: { naoIniciado: 1, andamento: 0, finalizado: 1 }
        },
        null  // Etapa 12: sem animação
    ];

    // Configuração das ações após etapas
    const stepAfterActions = [
        null, null, null, null, null,
        () => onHighlightButton('tutorial-btn-iniciar'), // Etapa 6
        () => onHighlightButton(null),                   // Etapa 7
        null, null, null, null, null
    ];

    const baseSteps: Step[] = [
        {
            target: '.tutorial-nao-iniciados',
            content: <TypewriterText text={stepTexts[0]} onComplete={() => executePendingAnimation(0)} />,
            placement: 'bottom',
            offset: 15,
            disableBeacon: true,
            floaterProps: {
                options: {
                    modifiers: {
                        flip: { enabled: false },
                        preventOverflow: { enabled: false }
                    }
                }
            }
        },
        {
            target: '.tutorial-andamento',
            content: <TypewriterText text={stepTexts[1]} onComplete={() => executePendingAnimation(1)} />,
            placement: 'bottom',
            offset: 15
        },
        {
            target: '.tutorial-finalizados',
            content: <TypewriterText text={stepTexts[2]} onComplete={() => executePendingAnimation(2)} />,
            placement: 'bottom',
            offset: 15
        },
        {
            target: 'body',
            content: <TypewriterText text={stepTexts[3]} onComplete={() => executePendingAnimation(3)} />,
            placement: 'center',
            disableBeacon: true
        },
        {
            target: '.tutorial-nao-iniciados',
            content: <TypewriterText text={stepTexts[4]} onComplete={() => executePendingAnimation(4)} />,
            placement: 'bottom',
            offset: 15,
            disableBeacon: true
        },
        {
            target: '.tutorial-btn-iniciar',
            content: <TypewriterText text={stepTexts[5]} onComplete={() => executePendingAnimation(5)} />,
            placement: 'top',
            offset: 15,
            disableBeacon: true
        },
        {
            target: '.tutorial-andamento',
            content: <TypewriterText text={stepTexts[6]} onComplete={() => executePendingAnimation(6)} />,
            placement: 'bottom',
            offset: 15,
            disableBeacon: true
        },
        {
            target: '.tutorial-voting-section',
            content: <TypewriterText text={stepTexts[7]} onComplete={() => executePendingAnimation(7)} />,
            placement: 'bottom',
            offset: 15,
            disableBeacon: true
        },
        {
            target: '.btn-finalizar-destacado, .tutorial-andamento .btn-danger',
            content: <TypewriterText text={stepTexts[8]} onComplete={() => executePendingAnimation(8)} />,
            placement: 'bottom',
            offset: 15,
            disableBeacon: true
        },
        {
            target: '.tutorial-finalizados',
            content: <TypewriterText text={stepTexts[9]} onComplete={() => executePendingAnimation(9)} />,
            placement: 'top',
            offset: 15,
            disableBeacon: true
        },
        {
            target: '.tutorial-btn-ausente',
            content: <TypewriterText text={stepTexts[10]} onComplete={() => executePendingAnimation(10)} />,
            placement: 'bottom',
            offset: 15,
            disableBeacon: true
        },
        {
            target: '.text-center.mt-5.pt-4 button',
            content: <TypewriterText text={stepTexts[11]} onComplete={() => executePendingAnimation(11)} />,
            placement: 'top',
            offset: 15,
            disableBeacon: true
        }
    ];

    // Adicionar etapas 13 e 14 condicionalmente
    const steps: Step[] = [...baseSteps];

    if (TUTORIAL_CONFIG.ENABLE_STEP_13) {
        steps.push({
            target: '.modal-content',
            content: <TypewriterText text="Modal de Finalizar Banca, indica-se que pode-se registrar ocorrências, caso existam" />,
            placement: 'center',
            disableBeacon: true
        });
    }

    if (TUTORIAL_CONFIG.ENABLE_STEP_14) {
        steps.push({
            target: '.modal-body .form-check',
            content: <TypewriterText text="Opção de enviar email para Candidatos" />,
            placement: 'right',
            disableBeacon: true
        });
    }

    useEffect(() => {
        if (isTutorialMode) {
            setRun(true);
            setStepIndex(0);
        } else {
            setRun(false);
        }
    }, [isTutorialMode]);

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isTutorialMode) {
                onTutorialComplete();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isTutorialMode, onTutorialComplete]);

    // Executar animação pendente após texto terminar
    const executePendingAnimation = (stepIndex: number) => {
        if (pendingAnimation && pendingAnimation.stepIndex === stepIndex) {
            executeAnimation(pendingAnimation.animation);
            setPendingAnimation(null);
        }
    };

    // Executar animação baseada na configuração
    const executeAnimation = (animation: any) => {
        if (!animation) return;

        switch (animation.type) {
            case 'fadeOutAll':
                setIsAnimating(true);
                setCandidatoNaoIniciadoOpacity(animation.opacity.naoIniciado);
                setCandidatoAndamentoOpacity(animation.opacity.andamento);
                setCandidatoFinalizadoOpacity(animation.opacity.finalizado);
                onOpacityChange(animation.opacity.naoIniciado, animation.opacity.andamento, animation.opacity.finalizado);
                setTimeout(() => setIsAnimating(false), animation.duration);
                break;

            case 'show':
            case 'move':
                setCandidatoNaoIniciadoOpacity(animation.opacity.naoIniciado);
                setCandidatoAndamentoOpacity(animation.opacity.andamento);
                setCandidatoFinalizadoOpacity(animation.opacity.finalizado);
                onOpacityChange(animation.opacity.naoIniciado, animation.opacity.andamento, animation.opacity.finalizado);
                break;

            case 'voteCount':
                let count = 0;
                const interval = setInterval(() => {
                    count++;
                    onUpdateVoteCount(count);
                    if (count >= animation.maxCount) {
                        clearInterval(interval);
                    }
                }, animation.duration);
                break;

            case 'fadeOut':
                setIsAnimating(true);
                const startTimeFadeOut = Date.now();
                const fadeOutInterval = setInterval(() => {
                    const elapsed = Date.now() - startTimeFadeOut;
                    const progress = Math.min(elapsed / animation.duration, 1);
                    const opacity = 1 - progress;
                    
                    if (animation.target === 'andamento') {
                        setCandidatoAndamentoOpacity(opacity);
                        onOpacityChange(0, opacity, 0);
                    }
                    
                    if (progress >= 1) {
                        clearInterval(fadeOutInterval);
                        setIsAnimating(false);
                    }
                }, 50);
                break;

            case 'fadeIn':
                setIsAnimating(true);
                if (animation.target === 'finalizado') {
                    setCandidatoFinalizadoOpacity(0);
                    onOpacityChange(0, 0, 0);
                } else if (animation.target === 'naoIniciado') {
                    setCandidatoNaoIniciadoOpacity(0);
                    onOpacityChange(0, 0, 0);
                }
                
                const startTimeFadeIn = Date.now();
                const fadeInInterval = setInterval(() => {
                    const elapsed = Date.now() - startTimeFadeIn;
                    const progress = Math.min(elapsed / animation.duration, 1);
                    const opacity = progress;
                    
                    if (animation.target === 'finalizado') {
                        setCandidatoFinalizadoOpacity(opacity);
                        onOpacityChange(0, 0, opacity);
                    } else if (animation.target === 'naoIniciado') {
                        setCandidatoNaoIniciadoOpacity(opacity);
                        onOpacityChange(opacity, 0, 0);
                    }
                    
                    if (progress >= 1) {
                        clearInterval(fadeInInterval);
                        setIsAnimating(false);
                    }
                }, 50);
                break;
        }
    };

    const handleJoyrideCallback = (data: CallBackProps) => {
        const { status, index, type } = data;

        // Atualizar índice da etapa
        if (type === 'step:after' || type === 'step:before' || type === 'tour:start') {
            setStepIndex(index);
        }



        // Verificar status de finalização
        if (status === STATUS.FINISHED || status === STATUS.SKIPPED || status === STATUS.ERROR) {
            setRun(false);
            onTutorialComplete();
            return;
        }

        // Preparar animação para executar após texto terminar
        if (type === 'step:before' && stepAnimations[index]) {
            setPendingAnimation({ stepIndex: index, animation: stepAnimations[index] });
        }

        // Executar ação após a etapa
        if (type === 'step:after' && stepAfterActions[index]) {
            stepAfterActions[index]!();
        }
    };



    return (
        <>
            <Joyride
                steps={steps}
                run={run}
                callback={handleJoyrideCallback}
                continuous={true}
                showProgress={false}
                showSkipButton={true}
                disableOverlayClose={false}
                disableCloseOnEsc={false}
                hideCloseButton={true}
                disableScrolling={false}
                locale={{
                    back: 'Voltar',
                    close: 'Fechar',
                    last: 'Finalizar',
                    next: 'Próximo',
                    skip: 'Sair'
                }}
                styles={{
                    options: {
                        primaryColor: '#007bff',
                        zIndex: 10000
                    },
                    tooltip: {
                        fontSize: '16px',
                        padding: '20px'
                    },
                    tooltipContent: {
                        padding: '10px 0 15px 0'
                    },
                    buttonNext: {
                        backgroundColor: '#007bff',
                        cursor: 'pointer',
                        marginLeft: '10px',
                        padding: '8px 16px'
                    },
                    buttonBack: {
                        color: '#6c757d',
                        marginRight: '10px',
                        padding: '8px 16px'
                    },
                    buttonSkip: {
                        color: '#6c757d',
                        fontSize: '12px',
                        marginRight: '15px'
                    },
                    tooltipFooter: {
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '15px',
                        paddingTop: '10px'
                    }
                }}

                floaterProps={{
                    disableAnimation: true,
                    styles: {
                        floater: {
                            filter: 'none'
                        }
                    },
                    options: {
                        modifiers: {
                            preventOverflow: {
                                enabled: false
                            },
                            flip: {
                                enabled: false
                            }
                        }
                    }
                }}
            />

            {/* Contador customizado x/y */}
            {run && (
                <style>
                    {`
                        .react-joyride__tooltip {
                            position: relative;
                        }
                        .react-joyride__tooltip::after {
                            content: "${stepIndex + 1}/${steps.length}";
                            position: absolute;
                            left: 50%;
                            transform: translateX(-50%);
                            font-size: 12px;
                            color: #6c757d;
                            font-weight: bold;
                            pointer-events: none;
                            background: rgba(255, 255, 255, 0.9);
                            padding: 2px 8px;
                            border-radius: 10px;
                            border: 1px solid #ccc;
                            ${[6, 10, 12].includes(stepIndex + 1) ? 
                                'top: -32px;' : 
                                'bottom: -32px;'
                            }
                        }
                        div[class*="react-joyride__overlay"] {
                            background-color: rgba(0, 0, 0, 0.4) !important;
                        }
                        .react-joyride__overlay {
                            background-color: rgba(0, 0, 0, 0.4) !important;
                        }

                    `}
                </style>
            )}

        </>
    );
};

export default TutorialVotacaoPresidente;